<template >
  <b-col v-if="summary !== undefined">
    <b-card class="h-100">
      <b-row align-h="between">
        <b-col md cols="12">
          <h4 class="card-title">{{projectName}}</h4>
          <h6 class="card-subtitle text-muted mb-2">{{ownerOrganization.name}}</h6>
        </b-col>
        <b-col md="auto" cols="6">
          <b-row>
            <b-col>
              <b-button
                v-if="isSystemAdmin"
                class="mr-1 mb-1"
                style="width: 100px;"
                :to="'/project/' + projectId">
                View Details
              </b-button>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <b-button
                v-if="isSystemAdmin"
                variant="primary"
                class="mr-1 mb-1"
                style="width: 100px;"
                :to="'/project/' + projectId + '#users-tab'">
                Edit Users
              </b-button>
              <b-button
                v-if="status !== 'Closed' && isSystemAdmin"
                variant="danger"
                class="mb-1"
                style="width: 100px;"
                :to="'/project/' + projectId + '/close'">
                Close Project
              </b-button>
            </b-col>
          </b-row>
        </b-col>
        <b-col md="auto" cols="6">
          {{startDate}} - {{endDate}}
          <br />
          RE: {{primaryOwner.name}}
          <br />
          PM: {{primaryContractor.name}}
          <br />
          <b-badge class="ml-1 mr-1" variant="primary">{{status}}</b-badge>
        </b-col>
      </b-row>
      <hr />
      <b-row>
        <b-col xl="3" md="6">
          <div v-if="summaryData.length > 0">
            <GChart
              type="LineChart"
              :data="summaryData"
              :options="summaryChartOptions"
              :resizeDebounce="1"
            />
          </div>
          <p v-if="!(summaryData.length > 0)">No responses received.</p>
        </b-col>
        <b-col xl="2" md="6">
          <GChart
            type="ColumnChart"
            :data="participation"
            :options="participationChartOptions"
            :resizeDebounce="1"
          />
        </b-col>
        <b-col xl="3" md="6">
          <ProjectMetricList
            :projectMetrics="openProjectMetrics"
            noneText="No incomplete action items!"
            :showDueDate="true"
            :showRemainingCount="true"
            :showLimit=5
            :showTypeBadge="true"
            :presetTags="projectTags"
            :cardClass="'mb-4 h-100'"
            @update="refreshProjectMetrics"
          ></ProjectMetricList>
        </b-col>
        <b-col xl="4" md="6" class="mb-4" style="text-align: center;">
          <b-row class="h-50 mb-4">
            <b-col cols="6">
              <MetricBox
                title="Dollars Saved"
                :data="dollarsSaved" 
                :isCurrency="true" 
                :compText="getBudgetText" 
                :compData="getBudgetRatio">
              </MetricBox>
            </b-col>
            <b-col cols="6">
              <b-card class="h-100" border-variant="success">
                <strong>Innovations</strong>
                <div class="metric-text">{{innovationCount}}</div>
              </b-card>
            </b-col>
          </b-row>
          <b-row class="h-50">
            <b-col cols="6">
              <MetricBox 
                title="Days Saved" 
                :data="daysSaved" 
                :compText="getScheduleText" 
                :compData="getScheduleRatio"
                :compDirectionFunc="() => '&#9650;'">
              </MetricBox>
            </b-col>
            <b-col cols="6">
              <b-card class="h-100" border-variant="success">
                <strong>Efficiencies</strong>
                <div class="metric-text">{{efficiencyCount}}</div>
              </b-card>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </b-card>
  </b-col>
</template>

<script>
import axios from "axios";
import { GChart } from "vue-google-charts";
import ProjectMetricList from "@/components/project/ProjectMetricList.vue";
import RespondentsList from "@/sub-components/RespondentsList.vue";
import MetricBox from "@/sub-components/MetricBox.vue";

export default {
  components: {
    GChart,
    ProjectMetricList,
    RespondentsList,
    MetricBox
  },
  props: {
    summary: Object
  },
  data() {
    if (this.summary === undefined) {
      return {};
    }

    let respondents = this.summary.projectUsers.filter(u => u.responseData != null)
      .map(respondent => ({
        ...respondent,
        isLoading: false,
        sent: false
      }));
    var owners = respondents.filter(r => r.role === "Owner");
    var contractors = respondents.filter(
      r => r.role === "Contractor"
    );
    var ownerRate = this.responseRate(owners);
    var contractorRate = this.responseRate(contractors);

    return {
      projectId: this.summary.project.projectId,
      surveyId: this.summary.project.survey ? this.summary.project.survey.surveyId : null,
      projectName: this.summary.project.projectName,
      periodId: this.summary.periodName,
      ownerOrganization: this.summary.project.ownerOrganization,
      primaryOwner: this.summary.project.primaryOwner,
      primaryContractor: this.summary.project.primaryContractor,
      status: this.summary.project.status,
      respondents: respondents,
      projectMetrics: this.summary.projectMetrics,
      owners: owners,
      contractors: contractors,
      projectTags: this.summary.project.presetTags,
      participation: [
        [
          "Entity",
          "Percent",
          { role: "style" },
          { role: "annotation" },
          { role: "tooltip" }
        ],
        [
          "Owner",
          ownerRate * 100,
          this.chartColor(ownerRate),
          this.ratePercentage(ownerRate),
          this.toolTip(this.responseCount(owners), owners.length)
        ],
        [
          "Contractor",
          contractorRate * 100,
          this.chartColor(contractorRate),
          this.ratePercentage(contractorRate),
          this.toolTip(this.responseCount(contractors), contractors.length)
        ]
      ],
      participationChartOptions: {
        title: "Response %",
        height: 275,
        animation: {
          startup: true,
          duration: 1500,
          easing: "out"
        },
        legend: {
          position: "none"
        },
        vAxis: {
          maxValue: 100,
          minValue: 0,
          gridlines: {
            color: "transparent"
          },
          textPosition: "none"
        },
        hAxis: {
          textStyle: {
            fontSize: 12
          }
        },
        titleTextStyle: {
          fontSize: 14
        },
        annotations: {
          textStyle: {
            fontSize: 12
          }
        }
      },
      summaryChartOptions: {
        title: "Average Score",
        height: 275,
        animation: {
          startup: true,
          duration: 1500,
          easing: "out"
        },
        legend: {
          position: "bottom"
        },
        pointSize: 4,
        pointShape: "square",
        chartArea: {
          width: "94%"
        },
        titleTextStyle: {
          fontSize: 14
        },
        hAxis: {
          slantedText: false
        },
        vAxis: {
          format: "0.0",
          minValue: 0.0,
          maxValue: 4.0,
          // viewWindow: {
          //   min: 0.0,
          //   max: 4.0
          // },
          ticks: [0.0, 1.0, 2.0, 3.0, 4.0],
          textPosition: "in"
        },
        colors: ["#34bf1b", "#4A708B"]
      },
      gradeProps: {
        width: 75,
        height: 75,
        center: true
      }
    };
  },
  methods: {
    responseCount(recipients) {
      return recipients.filter(r => r.responseData.responded).length;
    },
    responseRate(recipients) {
      return recipients.length === 0
        ? 1
        : this.responseCount(recipients) / recipients.length;
    },
    chartColor(rate) {
      if (rate > 0.75) {
        return "#32CD32";
      } else if (rate > 0.25) {
        return "#FCE205";
      } else {
        return "#FF0000";
      }
    },
    ratePercentage(rate) {
      return (rate * 100).toFixed(0) + "%";
    },
    toolTip(responses, invitations) {
      return `(${responses} of ${invitations})`;
    },
    showModal(name) {
      this.$refs[name].show();
    },
    showModalVFor(name) {
      this.$refs[name][0].show();
    },
    monthYearDate(date) {
      return new Date(date).toLocaleString("default", {
        month: "long",
        year: "numeric"
      });
    },
    refereshProjectMetrics() {
      let url = `${this.$api_url}/projects/${this.projectId}/project-metrics`;
      axios
        .get(url, this.getReqConfig())
        .then(response => {
          this.projectMetrics = response.data;
        })
        .catch(err => {
          if (err.response && err.response.statusText) {
            this.errors.push({ message: err.response.statusText });
          } else {
            this.errors.push(err);
          }
        });
    }
  },
  computed: {
    respondentsIncomplete() {
      return this.respondents.filter(r => r.responseData.responded === false);
    },
    openProjectMetrics() {
      return this.projectMetrics.filter(a => a.status === 'Open');
    },
    summaryData() {
      if (!(this.summary.responsesByMonth.length > 0)) {
        return [];
      }

      var data = this.$helpers.mapDataPointToChartDataSet(
        this.summary.responsesByMonth
      );
      if (data.length > 7) {
        let subset = [data[0]].concat(data.slice(-6))
        return subset;
      } else {
        return data;
      }
    },
    firstMonth() {
      if (this.summary.responsesByMonth.length === 0) {
        return null;
      }

      let allMonths = new Set(
        this.summary.responsesByMonth.map(m => new Date(m.categoryName))
      );

      return [...allMonths].sort((a, b) => a - b)[0];
    },
    lastMonth() {
      if (this.summary.responsesByMonth.length === 0) {
        return null;
      }

      let allMonths = new Set(
        this.summary.responsesByMonth.map(m => new Date(m.categoryName))
      );

      return [...allMonths].sort((a, b) => a - b)[allMonths.size - 1];
    },
    startDate() {
      return new Date(this.summary.project.startDate).toLocaleDateString("en-us", {
        month: "numeric",
        year: "2-digit"
      });
    },
    endDate() {
      if (this.summary.project.endDate) {
        return new Date(this.summary.project.endDate).toLocaleDateString("en-us", {
          month: "numeric",
          year: "2-digit"
        });
      } else {
        return "";
      }
    },
    dollarsSaved() {
      return this.projectMetrics.reduce((prev, cur) => prev + 
        ((cur.costSaved && !isNaN(cur.costSaved)) ? cur.costSaved : 0), 0);
    },
    daysSaved() {
      return this.projectMetrics.reduce((prev, cur) => prev + 
        ((cur.timeSaved && !isNaN(cur.timeSaved)) ? cur.timeSaved : 0), 0);
    },
    getBudgetText() {
      return this.getBudgetRatio > 0 ? "Over Budget" 
        : this.getBudgetRatio < 0 ? "Under Budget"
        : "";
    },
    getScheduleText() {
      return this.getBudgetRatio > 0 ? "Behind Schedule" 
        : this.getBudgetRatio < 0 ? "Ahead of Schedule"
        : "";
    },
    getBudgetRatio() {
      let cost = this.summary.project.projectCost;
      let saved = this.dollarsSaved;
      return cost && saved && cost > 0 ? parseFloat((-1 * saved) / cost) : 0;
    },
    getScheduleRatio() {
      let schedule = 0;
      if (this.summary.project.startDate && this.summary.project.endDate) {
        let start = new Date(this.summary.project.startDate);
        let end = new Date(this.summary.project.endDate);
        schedule = (end.getTime() - start.getTime()) /  (1000 * 3600 * 24);       
      }
      let saved = this.daysSaved;
      return schedule && saved && schedule > 0 ? parseFloat((-1 * saved) / schedule) : 0;
    },
    innovationCount() {
      return this.projectMetrics.filter(a => a.type === 'Innovation').length;
    },
    efficiencyCount() {
      return this.projectMetrics.filter(a => a.type === 'Efficiency').length;
    }
  }
};
</script>
<style scoped>
.scroll {
  max-height: 190px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}
.ellipsis {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.metric-text {
  font-size:2.5rem;
  line-height: 1;
  padding-top: 10px;
}
</style>